import { useState, KeyboardEvent, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Send } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
}

export const ChatInput = forwardRef<HTMLInputElement, ChatInputProps>(
  function ChatInput({ onSend, disabled }, ref) {
    const { t } = useTranslation('common')
    const [input, setInput] = useState('')

    const handleSend = () => {
      if (input.trim() && !disabled) {
        onSend(input)
        setInput('')
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    }

    return (
      <div className="border-t border-neutral-200 px-4 py-3 bg-white">
        <div className="flex gap-3 items-center">
          <input
            ref={ref}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatbot.placeholder')}
            disabled={disabled}
            className={cn(
              'flex-1 px-0 py-2 text-sm font-body',
              'border-0 border-b-2 border-neutral-200 bg-transparent',
              'focus:outline-none focus:border-primary',
              'transition-colors duration-300',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'placeholder:text-neutral-400'
            )}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled || !input.trim()}
            className={cn(
              'p-2.5 flex-shrink-0',
              'bg-primary text-white',
              'hover:bg-primary-dark transition-colors duration-200',
              'disabled:opacity-40 disabled:cursor-not-allowed'
            )}
            aria-label={t('chatbot.send')}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }
)
